import React, { useState, useEffect } from 'react';
import { useContent } from '../../context/ContentContext';
import { MapPin, ChevronDown, Bell, User } from 'lucide-react';
import { NavTabId } from '../common/BottomNav';
import {
  getCurrentCustomerProfile,
  getCustomerSessionToken,
} from '../../utils/supabase';
import { CustomerProfile } from '../../types';

interface NavbarProps {
  onOpenMember?: () => void;
  onOpenOrder?: () => void;
  activeTab?: NavTabId;
  onChangeTab?: (tab: NavTabId) => void;
  customerProfile?: CustomerProfile | null;
}

export const Navbar: React.FC<NavbarProps> = ({
  onOpenMember,
  onOpenOrder,
  activeTab = 'home',
  onChangeTab,
  customerProfile: externalCustomerProfile,
}) => {
  const { data } = useContent();
  const { siteSettings, branches } = data;
  const [activeSection, setActiveSection] = useState('home');
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(
    externalCustomerProfile ?? null
  );

  useEffect(() => {
    if (externalCustomerProfile !== undefined) {
      setCustomerProfile(externalCustomerProfile);
      if (!externalCustomerProfile && !getCustomerSessionToken()) {
        return;
      }
    }
    const token = getCustomerSessionToken();
    if (!token) {
      setCustomerProfile(null);
      return;
    }
    getCurrentCustomerProfile()
      .then((profile) => {
        setCustomerProfile(profile);
      })
      .catch(() => {});
  }, [
    externalCustomerProfile?.id,
    externalCustomerProfile?.userId,
    externalCustomerProfile?.namaLengkap,
  ]);

  useEffect(() => {
    const scrollContainer = document.getElementById('tab-container-home');
    const handleScroll = () => {
      const sections = ['home', 'menu-highlights', 'home-outlets', 'baristas', 'about', 'contact'];
      for (const sectionId of sections) {
        const el = document.getElementById(sectionId);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 240 && rect.bottom >= 240) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  const navItems: {
    label: string;
    id: string;
    href: string;
    tab?: NavTabId;
  }[] = [
    { label: 'Home', href: '#home', id: 'home', tab: 'home' },
    { label: 'Pesanan', href: '#pesanan', id: 'pesanan', tab: 'pesanan' },
    { label: 'Outlet', href: '#outlet', id: 'outlet', tab: 'outlet' },
    { label: 'Menu', href: '#menu', id: 'menu', tab: 'menu' },
    { label: 'Baristas', href: '#baristas', id: 'baristas' },
    { label: 'About', href: '#about', id: 'about' },
    { label: 'Contact', href: '#contact', id: 'contact' },
  ];

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    item: { label: string; id: string; href: string; tab?: NavTabId }
  ) => {
    e.preventDefault();

    if (item.tab && item.tab !== 'home') {
      if (onChangeTab) {
        onChangeTab(item.tab);
        return;
      }
    }

    if (onChangeTab && activeTab !== 'home') {
      onChangeTab('home');
    }

    setActiveSection(item.id);

    setTimeout(() => {
      if (item.id === 'home') {
        const homeScroll = document.getElementById('tab-container-home');
        if (homeScroll) {
          homeScroll.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
      const target = document.querySelector(item.href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }, 40);
  };

  const primaryOutletLabel = React.useMemo(() => {
    try {
      const saved = localStorage.getItem('leton_selected_outlet');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.name) {
          return parsed.name.replace(/^Leton\s+/i, '');
        }
      }
    } catch {}
    return branches && branches.length > 0
      ? `${branches[0].branchName}, Dumai`
      : 'Sudirman, Dumai';
  }, [branches]);

  const customerInitials = React.useMemo(() => {
    const rawName = (customerProfile?.namaLengkap || '').trim();
    if (!rawName) return '';
    const parts = rawName.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }, [customerProfile?.namaLengkap]);

  return (
    <div className="shrink-0 z-50">
      <header
        className="sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[#E2E8F0] transition-all duration-200"
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        <div className="max-w-lg lg:max-w-7xl mx-auto h-13 px-4 sm:px-6 flex items-center justify-between gap-2">
          {/* Left: Leton Logo & Brand Heading */}
          <button
            type="button"
            onClick={() => {
              if (onChangeTab) onChangeTab('home');
              const homeScroll = document.getElementById('tab-container-home');
              if (homeScroll) homeScroll.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            aria-label={siteSettings.brandName || 'LetonKu'}
            className="flex items-center group cursor-pointer text-left shrink-0"
          >
            <img
              src="/letonku-logo.png"
              alt={siteSettings.brandName || 'LetonKu'}
              width={941}
              height={214}
              decoding="async"
              className="h-[25px] sm:h-7 lg:h-8 w-auto object-contain select-none shrink-0"
            />
          </button>

          {/* Desktop Navigation Links */}
          <nav
            aria-label="Primary Navigation"
            className="font-body hidden lg:flex items-center gap-1 bg-[#F8FAFC] p-1 rounded-full border border-[#E2E8F0] shrink-0"
          >
            {navItems.map((item) => {
              const isActive =
                item.tab && item.tab !== 'home'
                  ? activeTab === item.tab
                  : activeTab === 'home' && activeSection === item.id;
              return (
                <a
                  key={item.id}
                  href={item.href}
                  id={`nav-link-${item.id}`}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`font-body px-3.5 py-1.5 rounded-full text-xs tracking-[-0.005em] transition-all whitespace-nowrap shrink-0 ${
                    isActive
                      ? 'bg-[#0056D2] text-white font-semibold shadow-2xs'
                      : 'text-[#64748B] font-medium hover:text-[#0A192F] hover:bg-white'
                  }`}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          {/* Right: Compact Outlet Selector, Notification & Account Profile */}
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <button
              type="button"
              onClick={() => {
                if (onChangeTab) {
                  onChangeTab('outlet');
                } else if (onOpenOrder) {
                  onOpenOrder();
                }
              }}
              className="font-body flex items-center gap-1 py-1.5 px-2.5 rounded-full bg-[#F8FAFC] hover:bg-[#F0F7FF] border border-[#E2E8F0] transition-colors cursor-pointer min-w-0"
            >
              <MapPin className="w-3 h-3 text-[#0056D2] shrink-0" />
              <span className="font-body text-[11px] leading-4 font-semibold tracking-[-0.005em] text-[#0A192F] truncate max-w-[100px] sm:max-w-[160px]">
                {primaryOutletLabel}
              </span>
              <ChevronDown className="w-3 h-3 text-[#64748B] shrink-0" />
            </button>

            <button
              type="button"
              onClick={() => {
                if (onChangeTab) {
                  onChangeTab('pesanan');
                } else if (onOpenMember) {
                  onOpenMember();
                }
              }}
              aria-label="Notifikasi Pesanan"
              className="w-8 h-8 rounded-full bg-[#F8FAFC] hover:bg-[#F0F7FF] border border-[#E2E8F0] flex items-center justify-center text-[#0A192F] transition-colors cursor-pointer shrink-0"
            >
              <Bell className="w-3.5 h-3.5 text-[#0056D2]" />
            </button>

            <button
              type="button"
              onClick={() => {
                if (onChangeTab) {
                  onChangeTab('akun');
                } else if (onOpenMember) {
                  onOpenMember();
                }
              }}
              aria-label="Profil Pelanggan"
              className="font-body w-8 h-8 rounded-full overflow-hidden border border-[#BAE6FD] bg-[#0056D2] text-white flex items-center justify-center text-[11px] font-semibold cursor-pointer shrink-0"
            >
              {customerInitials ? (
                <span>{customerInitials}</span>
              ) : (
                <User className="w-3.5 h-3.5 text-white" />
              )}
            </button>
          </div>
        </div>
      </header>
    </div>
  );
};
